/**
 * Generic animation helper that runs an update callback over a given duration.
 * @param {number} duration - Duration of the animation in milliseconds.
 * @param {function} updateCallback - A callback that receives the progress (0 to 1).
 */
function animateOverTime(duration, updateCallback) {
    let start = null;
    function animate(timestamp) {
        if (start === null) start = timestamp;
        const elapsed = timestamp - start;
        const progress = Math.min(elapsed / duration, 1);
        updateCallback(progress);
        if (progress < 1) {
            window.requestAnimationFrame(animate);
        }
    }
    window.requestAnimationFrame(animate);
}

/**
 * Toggles the slide up/down animation for an element.
 * @param {HTMLElement} element - The element to animate.
 * @param {number} duration - Animation duration in milliseconds.
 */
export function slideToggle(element, duration = 400) {
    const computedStyle = window.getComputedStyle(element);
    const isHidden = computedStyle.display === 'none';

    if (isHidden) {
        // Slide down: Prepare the element
        element.style.removeProperty('display');
        let display = window.getComputedStyle(element).display;
        if (display === 'none') display = 'block';
        element.style.display = display;
        element.style.overflow = 'hidden';

        // Use scrollHeight to measure the target height
        const targetHeight = element.scrollHeight;
        element.style.height = '0px';

        // Animate from 0 to targetHeight
        animateOverTime(duration, progress => {
            element.style.height = (progress * targetHeight) + 'px';
            if (progress === 1) {
                // Clean up after the animation is complete
                element.style.height = '';
                element.style.overflow = '';
            }
        });
    } else {
        // Slide up: Prepare the element for hiding
        element.style.overflow = 'hidden';
        const currentHeight = element.offsetHeight;
        element.style.height = currentHeight + 'px';

        // Animate from currentHeight down to 0
        animateOverTime(duration, progress => {
            element.style.height = (currentHeight * (1 - progress)) + 'px';
            if (progress === 1) {
                element.style.display = 'none';
                element.style.height = '';
                element.style.overflow = '';
            }
        });
    }
}

/**
 * @typedef {Object} BackendUserStatus
 * @property {boolean} loggedIn
 */

/**
 * Checks if the backend user is logged in by making a request to a specific endpoint.
 * Useful f. e. for enabling/disabling tracking.
 *
 * @returns {Promise<BackendUserStatus>}
 */
export const isBackendUserLoggedIn = async () => {
    const res = await fetch('/index.php?eID=isBackendUserLoggedIn');
    const json = await res.json();
    return json.loggedIn;
};
